import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Save, Grid3X3, Plus, Trash2, Calendar, Tag, 
  DollarSign, FileText, Copy, Check, AlertCircle
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

const DEFAULT_CATEGORIES = [
  'Rent', 'Electricity', 'Internet/Phone', 'Salaries & Advances', 'Transportation',
  'Fuel', 'Maintenance', 'Office Supplies', 'Packaging', 'Marketing',
  'Professional Fees', 'Insurance', 'Bank Fees', 'Software', 'Travel',
  'Meals', 'Raw Material', 'Other'
];

const PAYMENT_MODES = ['CASH', 'UPI', 'BANK', 'CARD', 'CHEQUE'];

const emptyRow = () => ({
  id: Date.now() + Math.random(),
  date: new Date().toISOString().split('T')[0],
  category: '',
  amount: '',
  payment_mode: 'CASH',
  vendor_name: '',
  reference_no: '',
  notes: '',
  isValid: false,
});

const BulkAddExpensesPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const bid = businessId || activeBusiness?.id;

  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState(() => Array(5).fill(null).map(() => emptyRow()));

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/expenses/categories/list');
      setCategories(data.categories || DEFAULT_CATEGORIES);
    } catch (e) {
      setCategories(DEFAULT_CATEGORIES);
    }
  };

  const updateRow = (index, field, value) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [field]: value };
    // Validate row
    newRows[index].isValid = !!(newRows[index].category && newRows[index].amount && parseFloat(newRows[index].amount) > 0);
    setRows(newRows);
  };

  const addRow = () => {
    setRows([...rows, emptyRow()]);
  };

  const removeRow = (index) => {
    if (rows.length > 1) {
      const newRows = rows.filter((_, i) => i !== index);
      setRows(newRows);
    }
  };

  const duplicateRow = (index) => {
    const newRow = { ...rows[index], id: Date.now() + Math.random() };
    const newRows = [...rows];
    newRows.splice(index + 1, 0, newRow);
    setRows(newRows);
  };

  const addMultipleRows = (count) => {
    const newRows = Array(count).fill(null).map(() => emptyRow());
    setRows([...rows, ...newRows]);
  };

  const clearAll = () => {
    if (window.confirm('Clear all rows?')) {
      setRows(Array(5).fill(null).map(() => emptyRow()));
    }
  };

  const validRows = rows.filter(r => r.isValid);
  const totalAmount = validRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  const handleSubmit = async () => {
    if (validRows.length === 0) {
      toast.error('Please fill in at least one valid expense');
      return;
    }

    setSaving(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const row of validRows) {
        const payload = {
          date: row.date,
          category: row.category,
          amount: parseFloat(row.amount),
          payment_mode: row.payment_mode,
          reference_no: row.reference_no || null,
          notes: row.notes || null,
        };

        try {
          await api.post('/expenses', payload);
          successCount++;
        } catch (e) {
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} expense(s) saved successfully`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} expense(s) failed to save`);
      }
      
      if (successCount > 0) {
        navigate(`/business/${bid}/expenses`);
      }
    } catch (error) {
      toast.error('Error saving expenses');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 px-6 py-4 shadow-sm">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-lg">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-slate-800">Bulk Add Expenses</h1>
              <p className="text-sm text-slate-500">Add multiple expenses at once in a spreadsheet-style view</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={clearAll} className="px-4 py-2 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium">
              Clear All
            </button>
            <button onClick={() => navigate(-1)} className="px-4 py-2 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium">
              Cancel
            </button>
            <button 
              onClick={handleSubmit} 
              disabled={saving || validRows.length === 0} 
              className="px-5 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : `Save ${validRows.length} Expense${validRows.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 py-6">
        {/* Summary Bar */}
        <div className="flex items-center justify-between mb-5 p-4 bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Check className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Valid Entries</p>
                <p className="text-lg font-bold text-emerald-600">{validRows.length}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Grid3X3 className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Rows</p>
                <p className="text-lg font-bold text-slate-700">{rows.length}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Amount</p>
                <p className="text-lg font-bold text-red-600">₹{totalAmount.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={addRow} className="px-3 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg text-sm font-medium flex items-center gap-1">
              <Plus className="w-4 h-4" /> Add Row
            </button>
            <button onClick={() => addMultipleRows(5)} className="px-3 py-2 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium">
              +5 Rows
            </button>
            <button onClick={() => addMultipleRows(10)} className="px-3 py-2 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium">
              +10 Rows
            </button>
          </div>
        </div>

        {/* Grid Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-12">#</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-32">Date *</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-40">Category *</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase w-28">Amount (₹) *</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-28">Mode</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-36">Vendor</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-32">Reference</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Notes</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.id} className={`border-b border-slate-100 ${row.isValid ? 'bg-emerald-50/30' : index % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                    <td className="px-3 py-2">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                        row.isValid ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {row.isValid ? <Check className="w-4 h-4" /> : index + 1}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="date"
                        value={row.date}
                        onChange={(e) => updateRow(index, 'date', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={row.category}
                        onChange={(e) => updateRow(index, 'category', e.target.value)}
                        className={`w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-red-500/20 ${
                          !row.category && row.amount ? 'border-red-300 bg-red-50' : 'border-slate-200'
                        }`}
                      >
                        <option value="">Select...</option>
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={row.amount}
                        onChange={(e) => updateRow(index, 'amount', e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        className={`w-full px-2 py-1.5 border rounded text-sm text-right font-medium focus:ring-2 focus:ring-red-500/20 ${
                          row.category && !row.amount ? 'border-red-300 bg-red-50' : 'border-slate-200'
                        }`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={row.payment_mode}
                        onChange={(e) => updateRow(index, 'payment_mode', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm"
                      >
                        {PAYMENT_MODES.map(mode => (
                          <option key={mode} value={mode}>{mode}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={row.vendor_name}
                        onChange={(e) => updateRow(index, 'vendor_name', e.target.value)}
                        placeholder="Vendor name"
                        className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={row.reference_no}
                        onChange={(e) => updateRow(index, 'reference_no', e.target.value)}
                        placeholder="Ref #"
                        className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={row.notes}
                        onChange={(e) => updateRow(index, 'notes', e.target.value)}
                        placeholder="Notes..."
                        className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => duplicateRow(index)}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Duplicate row"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeRow(index)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Remove row"
                          disabled={rows.length === 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add Row Footer */}
          <div className="p-4 bg-slate-50 border-t border-slate-200">
            <button
              onClick={addRow}
              className="w-full py-2.5 border-2 border-dashed border-slate-300 rounded-lg text-slate-500 hover:border-red-400 hover:text-red-600 hover:bg-red-50/50 transition-colors text-sm font-medium flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Row
            </button>
          </div>
        </div>

        {/* Tips */}
        <div className="mt-5 p-4 bg-amber-50 rounded-xl border border-amber-200">
          <p className="text-amber-800 font-medium text-sm mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Tips for Bulk Entry
          </p>
          <ul className="text-xs text-amber-700 space-y-1 ml-6">
            <li>• Category and Amount are required fields (marked with *)</li>
            <li>• Green highlighted rows are valid and will be saved</li>
            <li>• Use Tab key to quickly move between cells</li>
            <li>• Click "Duplicate" to copy a row for similar entries</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BulkAddExpensesPage;
